import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import { useAuthStore } from '@/auth/authStore'

export function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Set only when the business asks for a code as well, or a forgotten
  // password is being reset by one - the two land on the same code screen,
  // told apart by purpose. Until then this page is exactly the page it always
  // was. expiresAt is the moment the code stops working, held as a clock time
  // rather than a count, so the countdown shown from it survives the tab
  // being left and come back to.
  const [asking, setAsking] = useState<
    | { challenge: string | null; sentTo: string | null; expiresAt: number; purpose: 'signin' | 'reset' }
    | null
  >(null)
  const [code, setCode] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(0)
  // The one screen before a code exists for resetting a forgotten password -
  // just long enough to ask who it is for.
  const [forgotMode, setForgotMode] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)

  // Ticks once a second while a code is outstanding, and stops the moment it
  // is not - an interval left running after sign-in would do nothing but
  // burn a timer nobody is reading.
  useEffect(() => {
    if (!asking) return
    const tick = () => setSecondsLeft(Math.max(0, Math.round((asking.expiresAt - Date.now()) / 1000)))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [asking])
  const signIn = useAuthStore((state) => state.signIn)
  const signInWithCode = useAuthStore((state) => state.signInWithCode)
  const requestCode = useAuthStore((state) => state.requestCode)
  const forgotPassword = useAuthStore((state) => state.forgotPassword)
  const resetPassword = useAuthStore((state) => state.resetPassword)
  const navigate = useNavigate()

  /**
   * Where everybody lands.
   *
   * <p>My desk: what this person has to do today. The executive dashboard was
   * the old answer and it is the wrong one for almost everybody who signs in -
   * a cashier has no business being shown the month's pipeline, and half the
   * office cannot open that page at all.</p>
   */
  const home = '/my-desk'

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const outcome = await signIn(username.trim(), password)
      if (outcome.codeRequired) {
        setAsking({
          challenge: outcome.challenge,
          sentTo: outcome.sentTo,
          expiresAt: Date.now() + outcome.validMinutes * 60_000,
          purpose: 'signin',
        })
        setPassword('')
        return
      }
      navigate(home, { replace: true })
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Unable to sign in. Please try again.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function sendSignInCode() {
    setBusy(true)
    setError(null)
    try {
      const outcome = await requestCode(username.trim())
      if (outcome.codeRequired) {
        setAsking({
          challenge: outcome.challenge,
          sentTo: outcome.sentTo,
          expiresAt: Date.now() + outcome.validMinutes * 60_000,
          purpose: 'signin',
        })
      }
      setPassword('')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to send a code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function sendResetCode(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const outcome = await forgotPassword(username.trim())
      if (outcome.codeRequired) {
        setAsking({
          challenge: outcome.challenge,
          sentTo: outcome.sentTo,
          expiresAt: Date.now() + outcome.validMinutes * 60_000,
          purpose: 'reset',
        })
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Unable to send a code. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault()
    if (asking?.purpose === 'reset' && newPassword !== confirmPassword) {
      setError('Those two passwords are not the same.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (asking?.purpose === 'reset') {
        await resetPassword(asking.challenge ?? '', code.trim(), newPassword)
      } else {
        await signInWithCode(asking?.challenge ?? '', code.trim())
      }
      navigate(home, { replace: true })
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That code was not accepted.')
    } finally {
      setBusy(false)
    }
  }

  function startAgain() {
    setAsking(null)
    setCode('')
    setForgotMode(false)
    setNewPassword('')
    setConfirmPassword('')
    setError(null)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background: (theme) => theme.palette.appearance.hero,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Catering Platform
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Banquet halls, outdoor catering and bulk takeaway, from first enquiry to final payment.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {asking ? (
            <Box component="form" onSubmit={submitCode}>
              <Stack spacing={2}>
                <Typography variant="body2">
                  A code was sent to {asking.sentTo ?? 'your mobile'}.{' '}
                  {asking.purpose === 'reset'
                    ? 'Type it in, with the password to set.'
                    : 'Type it in to finish signing in.'}
                </Typography>
                <Typography variant="caption" color={secondsLeft > 0 ? 'text.secondary' : 'error'}>
                  {secondsLeft > 0
                    ? `Code valid for ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`
                    : 'This code has expired. Start again to get a new one.'}
                </Typography>
                <TextField
                  label="Code"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/[^0-9]/g, ''))}
                  // A phone keypad, and the browser offers the code it has just
                  // seen arrive rather than making somebody memorise it.
                  inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
                  autoFocus
                  fullWidth
                  required
                />
                {asking.purpose === 'reset' && (
                  <>
                    <TextField
                      label="New password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      autoComplete="new-password"
                      helperText="At least 10 characters"
                      fullWidth
                      required
                      inputProps={{ minLength: 10 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              edge="end"
                            >
                              {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      label="Confirm new password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      autoComplete="new-password"
                      fullWidth
                      required
                      inputProps={{ minLength: 10 }}
                    />
                  </>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy || secondsLeft <= 0}
                  fullWidth
                >
                  {asking.purpose === 'reset'
                    ? busy
                      ? 'Setting it…'
                      : 'Set new password and sign in'
                    : busy
                      ? 'Checking…'
                      : 'Sign in'}
                </Button>
                <Button onClick={startAgain} disabled={busy} fullWidth>
                  Start again
                </Button>
              </Stack>
            </Box>
          ) : forgotMode ? (
            <Box component="form" onSubmit={sendResetCode}>
              <Stack spacing={2}>
                <Typography variant="body2">
                  A code will be texted to the mobile number on your account. Type it in next to
                  set a new password.
                </Typography>
                <TextField
                  label="Username or mobile number"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  autoComplete="username"
                  autoFocus
                  fullWidth
                  required
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy || !username.trim()}
                  fullWidth
                >
                  {busy ? 'Sending…' : 'Send code'}
                </Button>
                <Button onClick={startAgain} disabled={busy} fullWidth>
                  Back to sign in
                </Button>
              </Stack>
            </Box>
          ) : (
          <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="Username or mobile number"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                autoFocus
                fullWidth
                required
                helperText="A mobile number only works for sign-in by OTP, below"
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
              <Button
                variant="text"
                disabled={busy || !username.trim()}
                onClick={() => void sendSignInCode()}
                fullWidth
              >
                Sign in with an OTP instead
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={busy}
                onClick={() => {
                  setForgotMode(true)
                  setError(null)
                }}
                fullWidth
              >
                Forgot password?
              </Button>
            </Stack>
          </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
